import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';

import 'maplibre-gl/dist/maplibre-gl.css';

import { MapProvider } from '@/hooks/useMap';
import { TILE_LAYERS, ensurePMTilesProtocol, type TileLayerKey } from '@/services/map-tiles';

export interface HuntMapProps {
  /** Map center as [lat, lng] */
  center: [number, number];
  zoom?: number;
  className?: string;
  /** Overlay UI rendered after map is ready (inside MapProvider) */
  children?: React.ReactNode;
  /** Called once when the map has loaded */
  onMapReady?: (map: maplibregl.Map) => void;
  initialStyle?: TileLayerKey;
  /**
   * When true, map initializes with pan/zoom disabled. The user must tap once
   * to activate interactions. Use on maps embedded in scrollable pages to
   * prevent accidental map interaction while scrolling.
   */
  requireTapToInteract?: boolean;
}

export function HuntMap({
  center,
  zoom = 14,
  className,
  children,
  onMapReady,
  initialStyle = 'default',
  requireTapToInteract = false,
}: HuntMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const [interactionEnabled, setInteractionEnabled] = useState(!requireTapToInteract);
  const rawMapRef = useRef<maplibregl.Map | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!containerRef.current) return;
    mountedRef.current = true; // reset on each effect invocation (guards against StrictMode double-run)

    ensurePMTilesProtocol();

    // MapLibre uses [lng, lat] — opposite of the app's [lat, lng] convention
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: TILE_LAYERS[initialStyle].style,
      center: [center[1], center[0]],
      zoom,
      attributionControl: false,
    });

    rawMapRef.current = map;

    if (requireTapToInteract) {
      map.scrollZoom.disable();
      map.dragPan.disable();
      map.touchZoomRotate.disable();
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!mountedRef.current) return;
      map.resize();
    });
    resizeObserver.observe(containerRef.current);

    map.on('load', () => {
      if (!mountedRef.current) return;
      setMapInstance(map);
      onMapReady?.(map);
      // E2E hook: expose map instance on window for Playwright tests. The hook
      // arms only when the VITE_E2E flag is the string "true" — set by dev:e2e
      // locally (via the gitignored .env.test) and by the CI "Build app" steps of
      // e2e.yml + visual-baseline.yml. In the real production bundle VITE_E2E is
      // unset, so Vite dead-code-eliminates this branch and the assignment is
      // absent. The guard compares against the literal string "true": Vite injects
      // the env value verbatim as a string, so a boolean `=== true` would never match.
      if (typeof window !== 'undefined' && import.meta.env.VITE_E2E === 'true') {
        // CAST: __mapInstance is a test-only E2E hook; not part of the standard Window interface
        (window as unknown as { __mapInstance?: maplibregl.Map }).__mapInstance = map;
      }
    });

    return () => {
      // Set flag first so any in-flight callbacks bail before touching the map
      mountedRef.current = false;
      rawMapRef.current = null;
      resizeObserver.disconnect();
      map.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — map is initialized once

  function handleActivate() {
    if (interactionEnabled) return;
    const map = rawMapRef.current;
    if (!map) return;
    map.scrollZoom.enable();
    map.dragPan.enable();
    map.touchZoomRotate.enable();
    setInteractionEnabled(true);
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative' }}
      onClick={requireTapToInteract && !interactionEnabled ? handleActivate : undefined}
      data-testid="hunt-map-container"
    >
      {mapInstance && (
        <MapProvider value={mapInstance}>
          {children}
        </MapProvider>
      )}
      {requireTapToInteract && !interactionEnabled && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center cursor-pointer"
          aria-label="Tap to interact with map"
          data-testid="map-tap-overlay"
        >
          <span className="bg-black/50 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none animate-fade-in">
            Tap to interact
          </span>
        </div>
      )}
    </div>
  );
}
